import { Box, Flex, Switch, Text, Tooltip } from '@chakra-ui/react';
import { useRouter } from 'next/router';
import { ChangeEvent, useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { InfoCircleIcon } from '~/components/icons/components/InfoCircleIcon';
import { FEATURE_FLAGS } from '~/constants';
import { useListAgencies } from '~/hooks/api/useListAgencies';
import { useFeatureFlag } from '~/hooks/useFeatureFlag';
import { useUpdateAgencyEnabledControls } from '~/screens/MyAgency/hooks/useAgencyUpdate';
import { useUpdateManagersEnabledControls } from '~/screens/MyAgency/hooks/useManagersUpdate';
import { selectTalentManager } from '~/selectors/talent-manager';

export const SwitchAudienceFilters: React.FC = () => {
  const router = useRouter();

  const agency = useListAgencies('');
  const manager = useSelector(selectTalentManager);

  const [audienceFiltersHidden, setAudienceFiltersHidden] = useState(false);

  const hideFilters = useFeatureFlag(FEATURE_FLAGS.foamRosterHideFilters);

  useEffect(() => {
    if (router.pathname === '/my-roster') {
      const isAudienceFiltersEnabled =
        manager?.enabledControls?.includes('audience-filters');

      if (typeof isAudienceFiltersEnabled === 'boolean') {
        setAudienceFiltersHidden(!isAudienceFiltersEnabled);
      }
      return;
    }

    // If agency is fetching, do nothing
    if (agency.isFetching) return;

    // typescript v5 update issue
    const isAudienceFiltersEnabled = (
      agency?.data as any
    )?.results[0].enabledControls.includes('audience-filters');

    if (typeof isAudienceFiltersEnabled === 'boolean') {
      setAudienceFiltersHidden(!isAudienceFiltersEnabled);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(agency), router.pathname, manager]);

  const { mutate: updateAgencyEnabledControls } =
    useUpdateAgencyEnabledControls();
  const { mutate: updateManagersEnabledControls } =
    useUpdateManagersEnabledControls();

  const handleSwitchAudienceFilters = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const checked = e.target.checked;
      setAudienceFiltersHidden(checked);

      const controls = !checked ? ['audience-filters'] : [];

      if (router.pathname === '/my-roster') {
        updateManagersEnabledControls(controls);
        return;
      }

      updateAgencyEnabledControls(controls);
    },
    [
      updateAgencyEnabledControls,
      updateManagersEnabledControls,
      router.pathname,
    ],
  );

  if (hideFilters !== 'enabled') return null;

  return (
    <Flex flexWrap="wrap" marginTop={4}>
      <Flex flex={1} gap={2}>
        <Text fontSize="md" fontWeight="400">
          Hide Audience Filters on Shared View
        </Text>
        <Tooltip
          hasArrow
          label="Audience performance metrics will not be displayed on the shared view."
          textAlign="center"
          width={64}
          paddingX={4}
          paddingY={2}
          borderRadius="4px"
          background="#1C2128"
          placement="top"
          fontSize="sm"
          fontWeight="400"
        >
          <Box as="span">
            <InfoCircleIcon boxSize={3} />
          </Box>
        </Tooltip>
      </Flex>
      <Switch
        isChecked={audienceFiltersHidden}
        onChange={handleSwitchAudienceFilters}
        track-id={
          audienceFiltersHidden
            ? 'foam-share-audiencefilters-off'
            : 'foam-share-audiencefilters-on'
        }
      />
    </Flex>
  );
};
